<template>
    <div class="general_info_card">
        <button class="card_header"
            :aria-expanded="isExpanded"
            @click="toggle">
            <span class="header_left is-flex is-align-items-center">
                <InfoCircle class="header_icon mr-2" />
                <span class="card_title">Información General</span>
            </span>
            <ArrowDown class="chevron"
                :class="{ 'is-rotated': isExpanded }" />
        </button>

        <transition name="info_collapse">
            <div v-if="isExpanded" class="info_body">
                <dl class="info_grid">
                    <div v-for="row in rows" :key="row.key" class="info_row">
                        <dt class="info_key">
                            <span>{{ row.label }}</span>
                        </dt>
                        <dd class="info_value">
                            <span v-if="row.type === 'pill'"
                                :class="['value_pill', row.pillClass]">
                                {{ row.value }}
                            </span>
                            <span v-else-if="row.value !== null && row.value !== ''">
                                {{ row.value }}
                            </span>
                            <span v-else class="value_empty">—</span>
                        </dd>
                    </div>
                </dl>
            </div>
        </transition>
    </div>
</template>

<script>
import { parseToMoney } from "@/utils/currency";

import ArrowDown from "@/components/svg/ArrowDown.vue";
import InfoCircle from "@/components/svg/InfoCircle.vue";

const SPANISH_MONTH_NAMES = [
    "enero", "febrero", "marzo", "abril", "mayo", "junio",
    "julio", "agosto", "septiembre", "octubre", "noviembre", "diciembre",
];

export default {
    name: "GeneralInfoCard",

    components: { ArrowDown, InfoCircle },

    props: {
        counterpart: { type: Object, default: null },
        // Total agregado del rango activo (suma de los `amount` prorrateados
        // de los docs visibles en la tabla). El selector del header del drawer
        // mueve este número en sincronía con la tabla.
        totalRange: { type: Number, default: 0 },
        // Cantidad de docs en el rango activo. Mismo origen que `totalRange`.
        documentsCount: { type: Number, default: 0 },
        // Label legible del rango — "Último año", "Últimos 6 meses", etc.
        // Se inyecta en los labels de los KPIs ("Total del último año").
        rangeLabel: { type: String, default: "" },
        firstActiveMonth: { type: String, default: null },
    },

    data() {
        return {
            // Default closed — la primera vista del drawer prioriza el chart
            // y la tabla. La info general es contexto que el usuario expande
            // si necesita validar identidad/clasificación.
            isExpanded: false,
        };
    },

    computed: {
        classifyValue() {
            const raw = this.counterpart?.classify_by_counterpart;
            if (!raw || raw === "Sin Clasificar") return null;
            return raw;
        },
        classifyPillClass() {
            const raw = this.counterpart?.classify_by_counterpart;
            if (!raw) return "";
            return `value_pill_${raw.toLowerCase().replace(/\s+/g, "_")}`;
        },
        typePillClass() {
            const raw = this.counterpart?.counterpart_type;
            if (!raw) return "";
            return `value_pill_${raw.toLowerCase().replace(/\s+/g, "_")}`;
        },
        firstDocLabel() {
            if (!this.firstActiveMonth) return null;
            const [year, month] = this.firstActiveMonth.split("-");
            const idx = Number(month) - 1;
            if (idx < 0 || idx > 11) return null;
            // Granularidad mensual — el endpoint historial agrupa por mes.
            // Lo expresamos como "<mes> de <año>" en lugar de un día falso.
            return `${SPANISH_MONTH_NAMES[idx]} de ${year}`;
        },
        rows() {
            const cp = this.counterpart || {};
            return [
                { key: "rut", label: "RUT", value: cp.rut || null },
                { key: "alias", label: "Razón Social", value: cp.alias || null },
                {
                    key: "type",
                    label: "Tipo de Contraparte",
                    value: cp.counterpart_type || null,
                    type: cp.counterpart_type ? "pill" : null,
                    pillClass: this.typePillClass,
                },
                {
                    key: "classify",
                    label: "Clasificación",
                    value: this.classifyValue,
                    type: this.classifyValue ? "pill" : null,
                    pillClass: this.classifyPillClass,
                },
                {
                    key: "first_doc",
                    label: "Fecha del primer documento",
                    value: this.firstDocLabel,
                },
                // KPIs alineados al rango activo del selector. Antes existía
                // un "Total del último año" separado (basado en monthly_history),
                // pero quedó redundante cuando docs + total se movieron al
                // mismo rango: ambos KPIs apuntan al mismo monto.
                {
                    key: "range_total",
                    label: this.rangeLabel ? `Total ${this.rangeLabel.toLowerCase()}` : "Total del rango",
                    value: this.totalRange ? parseToMoney(this.totalRange) : "$0",
                },
                {
                    key: "docs_count",
                    label: this.rangeLabel ? `Documentos ${this.rangeLabel.toLowerCase()}` : "Documentos del rango",
                    value: this.documentsCount,
                },
            ];
        },
    },

    methods: {
        toggle() {
            this.isExpanded = !this.isExpanded;
        },
    },
};
</script>

<style scoped lang="scss">
.general_info_card {
    background: var(--report-bg);
    border: 1px solid var(--report-border);
    border-radius: 12px;
    box-shadow: 0 1px 2px rgba(10, 13, 20, 0.03);
    overflow: hidden;
}

.card_header {
    width: 100%;
    background: transparent;
    border: none;
    padding: 16px 20px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    cursor: pointer;
    text-align: left;

    &:hover {
        background: var(--report-surface);
    }
}

.header_icon {
    width: 18px;
    height: 18px;
    color: var(--report-text-muted-strong);
}

.card_title {
    font-size: 14px;
    font-weight: 600;
    color: var(--report-text);
}

.chevron {
    width: 16px;
    height: 16px;
    color: var(--report-text-muted-strong);
    transition: transform 180ms ease;

    &.is-rotated {
        transform: rotate(180deg);
    }
}

.info_body {
    border-top: 1px solid var(--report-border);
    padding: 8px 20px 16px 20px;
}

.info_grid {
    margin: 0;
}

.info_row {
    display: grid;
    grid-template-columns: minmax(200px, 280px) 1fr;
    align-items: center;
    padding: 10px 0;
    border-bottom: 1px solid var(--report-divider-soft);

    &:last-child {
        border-bottom: none;
    }
}

.info_key {
    display: flex;
    align-items: center;
    font-size: 13px;
    color: var(--report-text-muted-strong);
    font-weight: 400;
    margin: 0;
}

.info_value {
    font-size: 13px;
    color: var(--report-text);
    font-weight: 500;
    margin: 0;
    text-align: right;
    justify-self: end;
}

.value_empty {
    color: var(--report-text-muted-soft);
    font-weight: 400;
}

.value_pill {
    display: inline-flex;
    align-items: center;
    padding: 2px 10px;
    border-radius: 12px;
    font-size: 12px;
    font-weight: 500;
    background: var(--report-bg);
    border: 1px solid var(--report-border);
    color: var(--report-text-muted-strong);
}

.value_pill_cliente {
    background: var(--counterpart-cliente-bg);
    color: var(--counterpart-cliente-text);
    border-color: var(--counterpart-cliente-border);
}

.value_pill_proveedor,
.value_pill_internacional {
    background: var(--report-accent-bg);
    color: var(--report-accent-text);
    border-color: var(--report-accent-border);
}

.value_pill_honorario,
.value_pill_trabajador {
    background: var(--counterpart-honorario-bg);
    color: var(--counterpart-honorario-text);
    border-color: var(--counterpart-honorario-border);
}

.value_pill_contraparte {
    background: var(--report-accent-bg);
    color: var(--report-accent-text);
    border-color: var(--report-accent-border);
}

.value_pill_documento {
    background: var(--counterpart-recibo-bg);
    color: var(--counterpart-recibo-text);
    border-color: var(--counterpart-recibo-border);
}

.value_pill_mixta {
    background: #f5f3ff;
    color: #5925dc;
    border-color: #d9d6fe;
}

.info_collapse-enter-active,
.info_collapse-leave-active {
    transition: opacity 180ms ease, max-height 220ms ease;
    overflow: hidden;
    max-height: 600px;
}

.info_collapse-enter,
.info_collapse-leave-to {
    opacity: 0;
    max-height: 0;
}
</style>
